import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Form, Button, InputGroup, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// hooks
import { useApi } from 'landing-page/hooks/useApi';
import { api, AlumniType, TalentaCardQueryParamsType, ProgramBeasiswaType } from 'landing-page/helpers/api';

// layout
import MainLayout from 'landing-page/components/layouts/MainLayout';

// components
import Section from 'landing-page/components/common/Section';
import Container from 'landing-page/components/common/Container';
import Hero from 'landing-page/components/alumni/Hero';
import CTA from 'landing-page/components/layout/CTA';
import { AlumniCard } from 'landing-page/components/home/Alumni';
import { AlumniCardPlaceholder } from 'landing-page/components/home/Alumni/Placeholder';

// styles
import './styles.scss';

// Search form interface
interface SearchFormData {
  q: string;
  program: string;
}

// Program beasiswa options (added manually)
const PROGRAM_BEASISWA_OPTIONS = [
  { value: 'Semua Program', label: 'Semua Program' },
] as const;

const Alumni: React.FC = () => {
  const navigate = useNavigate();

  // Search form state
  const [searchForm, setSearchForm] = useState<SearchFormData>({
    q: '',
    program: 'Semua Program',
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // API state
  const { data, loading, error, execute: fetchAlumniData } = useApi(api.getPublishedAlumni.bind(api));

  const { data: programBeasiswaData, loading: programBeasiswaLoading, error: programBeasiswaError, execute: fetchProgramBeasiswaData } = useApi(api.getProgramBeasiswa.bind(api));
  
  const [alumniItems, setAlumniItems] = useState<AlumniType[]>([]);

  const [programBeasiswaItems, setProgramBeasiswaItems] = useState<ProgramBeasiswaType[]>([]);
  // Debounced search to avoid too many API calls
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchForm.q);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchForm.q]);

  // Build query parameters
  const queryParams = useMemo((): TalentaCardQueryParamsType => {
    const params: TalentaCardQueryParamsType = {
      page: currentPage,
      per_page: 12, // Show 12 items per page
      q: '',
      program: '',
    };

    if (debouncedSearch.trim()) {
      params.q = debouncedSearch.trim();
    }

    if (searchForm.program) {
      params.program = searchForm.program;
    }

    return params;
  }, [debouncedSearch, searchForm.program, currentPage]);

  // Fetch alumni data when query parameters change
  useEffect(() => {
    fetchAlumniData(queryParams);
  }, [queryParams]);

  useEffect(() => {
    fetchProgramBeasiswaData();
  }, []);

  useEffect(() => {
    if (programBeasiswaData?.data?.items) {
      setProgramBeasiswaItems([...programBeasiswaData.data.items, ...PROGRAM_BEASISWA_OPTIONS]);
    }
  }, [programBeasiswaData]);

  // Update alumni items and pagination when data changes
  useEffect(() => {
    if (data?.data?.profiles) {
      setAlumniItems(data.data.profiles.data);
      setTotalPages(data.data.profiles.last_page);
      setTotalItems(data.data.profiles.total);
    }
  }, [data]);

  // Handle search form changes
  const handleSearchChange = useCallback((field: keyof SearchFormData, value: string) => {
    setSearchForm(prev => ({
      ...prev,
      [field]: value,
    }));
    // Reset to first page when search changes
    setCurrentPage(1);
  }, []);

  // Handle search submission
  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    // The search will be triggered automatically by the debounced effect
    setCurrentPage(1);
  }, []);

  // Handle reset
  const handleReset = useCallback(() => {
    setSearchForm({
      q: '',
      program: 'Semua Program',
    });
    setCurrentPage(1);
  }, []);

  // Handle view story
  const handleViewStory = useCallback((alumni: AlumniType) => {
    navigate(`/flip-book/${alumni.id}`);
  }, [navigate]);

  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    // Scroll to top of alumni section
    document.getElementById('alumni')?.scrollIntoView({ 
      behavior: 'smooth' 
    });
  }, []);

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <Button
        key="prev"
        variant="outline-primary"
        size="sm"
        disabled={currentPage === 1}
        onClick={() => handlePageChange(currentPage - 1)}
        className="me-2"
      >
        Sebelumnya
      </Button>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? "primary" : "outline-primary"}
          size="sm"
          onClick={() => handlePageChange(i)}
          className="me-2"
        >
          {i}
        </Button>
      );
    }

    // Next button
    pages.push(
      <Button
        key="next"
        variant="outline-primary"
        size="sm"
        disabled={currentPage === totalPages}
        onClick={() => handlePageChange(currentPage + 1)}
      >
        Selanjutnya
      </Button>
    );

    return (
      <div className="d-flex justify-content-center align-items-center mt-4">
        <div className="d-flex align-items-center">
          {pages}
        </div>
        <div className="ms-3 text-muted small">
          Menampilkan {((currentPage - 1) * 12) + 1}-{Math.min(currentPage * 12, totalItems)} dari {totalItems} alumni
        </div>
      </div>
    );
  };

  return (
    <MainLayout>
      <Hero
        autoFetch={true}
        title="Featured Alumni"
        subtitle="Mengenal lebih dekat alumni LPDP yang telah berkontribusi membangun Indonesia melalui pendidikan berkualitas dunia"
        primaryButton={{
          label: 'Lihat Alumni',
          href: '#alumni',
          variant: 'primary',
          onClick: () => {
            document.getElementById('alumni')?.scrollIntoView({ 
              behavior: 'smooth' 
            });
          },
        }}
      />

      <Section className='py-15' id='alumni' variant='white'>
        <Container>
          {/* Search Form */}
          <Card className='shadow-sm mb-10'>
            <Card.Body>
              <Form onSubmit={handleSearch}>
                <InputGroup className='w-100 flex-nowrap'>
                  <Form.Control 
                    type='text' 
                    placeholder='Cari alumni berdasarkan nama, universitas, atau bidang studi...' 
                    className='rounded-5 w-75'
                    value={searchForm.q}
                    onChange={(e) => handleSearchChange('q', e.target.value)}
                    disabled={loading}
                  />
                  <Form.Select 
                    aria-label="Pilih program"
                    className='rounded-5'
                    value={searchForm.program}
                    onChange={(e) => handleSearchChange('program', e.target.value)}
                    disabled={loading}
                  >
                    {programBeasiswaItems.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Button 
                    variant='primary' 
                    className='rounded-5'
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Mencari...
                      </>
                    ) : (
                      'Cari'
                    )}
                  </Button>
                  <Button 
                    variant='outline-primary' 
                    className='rounded-5'
                    type="button"
                    onClick={handleReset}
                    disabled={loading}
                  >
                    Reset
                  </Button>
                </InputGroup>
              </Form>
              {/* Search Results Info */}
              {!loading && !error && (
                <div className="mt-4">
                  <h5 className="text-muted fs-5 fw-normal" style={{textTransform: 'unset'}}>
                    {debouncedSearch || searchForm.program ? (
                      <>
                        Hasil pencarian untuk "{debouncedSearch || 'Semua Program'}"
                        {searchForm.program && ` - ${programBeasiswaItems.find(opt => opt.value === searchForm.program)?.label}`}
                        {' '}({totalItems} alumni ditemukan)
                      </>
                    ) : ( 
                      `Semua Alumni (${totalItems} alumni)`
                    )}
                  </h5>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert variant="danger" className="mb-4">
              <Alert.Heading>Terjadi Kesalahan</Alert.Heading>
              <p>{error.message || 'Gagal memuat data alumni. Silakan coba lagi.'}</p>
            </Alert>
          )}

          {/* Alumni Grid */}
          <div className='alumni-grid'>
            {loading && alumniItems.length === 0 && (
              [0,1,2,3].map((index) => (
                <div key={index} className="alumni-card-item">
                  <AlumniCardPlaceholder />
                </div>
              ))
            )}

            {!loading && alumniItems.length > 0 && alumniItems.map(alumni => (
              <div key={alumni.id} className="alumni-card-item">
                <AlumniCard alumni={alumni} onViewStory={handleViewStory} />
              </div>
            ))}
          </div>

          {!loading && alumniItems.length === 0 && !error && (
            <div className="col-12 text-center py-5 w-100">
              <div className="text-muted">
                <i className="fas fa-search fa-3x mb-3"></i>
                <h4 style={{textTransform: 'unset'}}>Tidak ada alumni yang ditemukan</h4>
                <p>Coba ubah kata kunci pencarian atau filter program beasiswa.</p>
              </div>
            </div>
          )}

          {/* Pagination */}
          {renderPagination()}
        </Container>
      </Section>

      <Section className='py-15' variant='white' id='cta'>
        <CTA />
      </Section>
    </MainLayout>
  )
}

export default Alumni;